import React, { useEffect, useMemo, useRef, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import AutoSizer from 'react-virtualized-auto-sizer';

// LOCAL VARIABLES
import { ROW_HEIGHT } from '../variables';

// HELPERS
import scrollbarWidth from '../helpers/scrollbarWidth';
import { useListData } from 'helpers/getListData';
import usePrevious from 'helpers/usePrevious';

// LOCAL COMPONENTS
import SortedVirtualized from './layout/SortedVirtualized';

// STYLES
import styles from './tableSorted.module.scss';

// FUNCTIONS
const stickElement = table => {
  const head = document.head || document.getElementsByTagName('head')[0];
  if (!document.getElementById('sticky-style')) {
    const style = document.createElement('style');
    style.setAttribute("id", 'sticky-style');
    head.appendChild(style);
  }
  const handleScroll = () => {
    document.getElementById('sticky-style').innerHTML = `.table-sticky { margin-left: ${table.scrollLeft}px }`;
  }
  table.addEventListener('scroll',  handleScroll);
  return () => {
    table.removeEventListener('scroll',  handleScroll);
    document.getElementById('sticky-style').remove();
  }
}

// MAIN COMPONENT
export default function TableSorted({
  autoSizeHeight = true, //
  className,
  defaultTitle,
  disableResizing,
  rowHeight = ROW_HEIGHT,
  showNumber,
  sortedResult,
  sortParam,
  ...props
}) {
  // REFS
  const tableRef = useRef();
  const [bodyRef, setBodyRef] = useState({});

  // STATE
  const [totalColumnsWidth, setTotalColumnsWidth] = useState(0);

  // HOOKS / PROPS
  const {
    result: data = [], //
    preferences: { filters } = {}
  } = Object.assign(useListData() || {}, props);

  // MEMOS
  const groupedData = useMemo(
    () =>
      sortedResult ||
      data.reduce((acc, cur) => {
        const sortKey = sortParam || filters?.sort;
        const title = cur[sortKey] || defaultTitle;
        if (!!title) {
          const child = acc.find(obj => obj.title === title);
          if (!child)
            acc.push({
              title,
              data: [cur]
            });
          else child.data.push(cur);
        }
        return acc;
      }, []),
    [data, defaultTitle, filters.sort, sortParam, sortedResult]
  );
  useEffect(
    () => {
      return stickElement(tableRef.current);
    },
    [tableRef]
  );

  const scrollbarSize = useMemo(() => scrollbarWidth(), []);

  const { scrollHeight, clientHeight } = bodyRef || {};

  const verticalScrollbarWidth = useMemo(() => {
    return scrollHeight > clientHeight ? scrollbarSize : 0;
  }, [scrollHeight, clientHeight, scrollbarSize]);

  const prevDataLength = usePrevious(data.length);

  // EFFECTS
  // If resetting list (on sort or filter), scroll to top
  useEffect(() => {
    if (bodyRef?.current && prevDataLength && data.length < prevDataLength) {
      bodyRef.current.scrollTop = 0;
    }
  }, [prevDataLength, data.length, bodyRef]);

  // RENDER
  return (
    <div
      className={clsx(styles.container, 'table-responsive flex-grow-1 d-flex flex-column', className)}
      ref={tableRef}
    >
      <AutoSizer disableHeight={!autoSizeHeight}>
        {({ height, width }) => {
          const tableWidth = Math.max(width, totalColumnsWidth + verticalScrollbarWidth);
          const headerWidth = tableWidth + parseInt(styles['padding-x']) * 2;
          return (
            <SortedVirtualized
              {...props}
              headerWidth={headerWidth}
              height={autoSizeHeight ? height - scrollbarSize : undefined}
              numLoaded={groupedData.length}
              ref={bodyRef}
              rowHeight={rowHeight}
              result={groupedData}
              setRef={setBodyRef}
              setTotalColumnsWidth={setTotalColumnsWidth}
              width={tableWidth}
              verticalScrollbarWidth={verticalScrollbarWidth}
            />
          );
        }}
      </AutoSizer>
    </div>
  );
}
